/**
 * Re-orders options after a filter (e.g. substring from `createFilterOptions`) so stronger matches appear first:
 * exact label, whole-string prefix, word-prefix, then arbitrary substring.
 * Expects `options` to already be the filtered subset for the current query.
 */
export function rankFilteredOptionsByInputMatch<T>(
  options: ReadonlyArray<T>,
  inputValue: string,
  getLabel: (option: T) => string
): T[] {
  const list = [...options] as T[];
  const rawQuery = inputValue.trim();
  if (!rawQuery) {
    return list;
  }
  const q = rawQuery.toLowerCase();

  const rank = (label: string): number => {
    const l = label.trim().toLowerCase();
    if (!l.includes(q)) return 99;
    if (l === q) return 0;
    if (l.startsWith(q)) return 1;
    const words = l.split(/[^a-z0-9]+/).filter(Boolean);
    if (words.some((w) => w.startsWith(q))) return 2;
    return 3;
  };

  return list.sort((a, b) => {
    const ra = rank(getLabel(a));
    const rb = rank(getLabel(b));
    if (ra !== rb) return ra - rb;
    return getLabel(a).localeCompare(getLabel(b), undefined, { sensitivity: 'base' });
  });
}

/**
 * MUI Autocomplete `filterOptions`: substring match on `getSearchText(option)`, ranked so name-prefix and
 * word-prefix hits (e.g. "Se" → "Se cap") surface above weak substring matches ("…InSe3…").
 * Use plain-text search strings (e.g. `name`), not HTML labels.
 */
export function filterAutocompleteOptionsByMatchRank<T>(
  options: readonly T[],
  params: { inputValue: string },
  getSearchText: (option: T) => string
): T[] {
  const q = params.inputValue.trim().toLowerCase();
  if (!q) return [...options];

  const scored: { opt: T; rank: number }[] = [];
  for (const opt of options) {
    const name = getSearchText(opt).trim().toLowerCase();
    if (!name.includes(q)) continue;

    let rank: number;
    if (name.startsWith(q)) {
      rank = 0;
    } else {
      const words = name.split(/[^a-z0-9]+/).filter(Boolean);
      rank = words.some((w) => w.startsWith(q)) ? 1 : 2;
    }
    scored.push({ opt, rank });
  }

  scored.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return getSearchText(a.opt).localeCompare(getSearchText(b.opt), undefined, { sensitivity: 'base' });
  });
  return scored.map((s) => s.opt);
}
